import React, { useContext, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import AlwaysOnRoomOption from './AlwaysOnRoomOption'
import LayoutContainer from 'components/Common/LayoutContainer'
import { mediaQueryUp } from 'components/utils/breakpoint'

import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import {
  getDurationPackageOptions,
  getLowestAvailableStayPayTier,
} from 'selectors/offerPage/offerPageSelectors'
import Caption from 'LuxKit/Typography/Caption'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { buildRoomPackageOptionsViewOrderLPC, preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import type { OfferPageRoomOption } from 'contexts/OfferPage/offerPageStateReducer'
import { initialOfferPageRooms } from 'contexts/OfferPage/offerPageStateReducer'
import config from 'constants/config'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import useOfferAvailableRates from 'hooks/useOfferAvailableRates'
import ContactContext from 'contexts/contactContext'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'

const PackagesLayoutContainer = styled(LayoutContainer)`
  padding: 0;

  ${mediaQueryUp.desktop} {
    padding: 0 ${rem(16)};
  }
`

interface Props {
  offer: App.Offer
  rooms: Array<App.Occupants>
  checkIn?: string
  packageOptions: Array<App.OfferPackageOptionView>
  airport?: App.Airport
  roomOption?: OfferPageRoomOption
  showFlightInformation?: boolean
  destinationAirport?: App.Airport
  onBookNowInvalid?: () => void
  onViewPackageOptions: () => void
  lowestAvailableStayPayTier?: App.StayPayTier | null
}

function AlwaysOnRoomOptions(props: Props) {
  const {
    offer,
    packageOptions,
    showFlightInformation,
    destinationAirport,
    roomOption,
    onViewPackageOptions,
    checkIn,
    rooms,
    lowestAvailableStayPayTier,
  } = props

  const intl = useIntl()
  const supportContact = useContext(ContactContext)
  const hasRoomOption = roomOption && Object.entries(roomOption).length === 2
  const packageOptionsByRoom = useMemo(() => {
    return buildRoomPackageOptionsViewOrderLPC(packageOptions)
  }, [packageOptions])

  const highlightedPackage = hasRoomOption ?
      {
        roomId: roomOption?.roomTypeId,
        packageViews: packageOptionsByRoom.find(item => item.roomId === roomOption?.roomTypeId)?.packageViews ?? [],
      } : null

  const packageDuration = packageOptionsByRoom?.[0]?.packageViews?.[0]?.package?.duration
  const checkOut = useMemo(() => checkIn ? moment(checkIn).add(packageDuration, 'days').format(ISO_DATE_FORMAT) : undefined, [checkIn, packageDuration])

  const offerAvailableRates = useOfferAvailableRates(offer?.id, {
    checkIn,
    checkOut,
    rooms: rooms ?? initialOfferPageRooms,
  })

  const hasRateError = !!offerAvailableRates?.error

  return (<VerticalSpacer gap={16}>
    <LayoutContainer>
      {hasRateError && <MessageBanner
        kind="warning"
        tabletAlign="center"
        heading={intl.formatMessage(
          {
            id: 'NqkfLhKLHyWL68AMKz0O',
            defaultMessage: 'The third party booking system for {propertyName} is currently down',
            description: 'Warning heading when the hotel booking system is unavailable',
          },
          { propertyName: offer.property?.name },
        )}
      >
        <FormattedMessage
          id="6Ik4jaitlAq16MsoHuLG"
          defaultMessage="Please call our VIP team on {phoneNumber} who are ready to provide assistance to complete your booking"
          description="Message asking user to call VIP support when the booking system is down"
          values={{ phoneNumber: supportContact.local.humanReadable }}
        />
      </MessageBanner>}

    </LayoutContainer>
    {!hasRateError && <PackagesLayoutContainer>
      <VerticalSpacer gap={32}>
        {!checkIn && lowestAvailableStayPayTier && <MessageBanner kind="promotional-primary" icon={<LineInfoCircleIcon />}>
          <FormattedMessage
            id="d-wQ0D06BpRV72CiYRCN"
            defaultMessage="<bold>Special offer:</bold> Enjoy free nights for stays longer than {nights, plural, one {# day} other {# days}}. Select dates to check eligibility."
            description="Promotional banner about free nights for longer hotel stays"
            values={{
              bold: (chunks) => <b>{chunks}</b>,
              nights: lowestAvailableStayPayTier.stayNights,
            }}
          />
        </MessageBanner>}
        {hasRoomOption && !!highlightedPackage?.packageViews.length && <AlwaysOnRoomOption
          key={highlightedPackage.roomId}
          offer={offer}
          packageViews={highlightedPackage.packageViews}
          flightDestination={destinationAirport}
          isHighlighted
          onViewPackageOptions={onViewPackageOptions}
        />}
        {packageOptionsByRoom.map((item) => {
          if (hasRoomOption && roomOption.roomTypeId === item.roomId) {
            return null
          }
          return (<AlwaysOnRoomOption
            key={item.roomId}
            offer={offer}
            packageViews={item.packageViews}
            flightDestination={destinationAirport}
            onViewPackageOptions={onViewPackageOptions}
          />)
        })}
      </VerticalSpacer>
    </PackagesLayoutContainer>}
    <LayoutContainer>
      {showFlightInformation && <Caption variant="medium" align="center">
        <FormattedMessage
          id="KcpXrkWAC9IDZccPZ-vo"
          defaultMessage="*Price is indicative and based on sample return fares (inclusive of booking fees on Low Cost Carriers). To view live pricing, conduct a full search. {brandName} full Terms and Conditions apply."
          description="Disclaimer about indicative flight pricing and terms"
          values={{ brandName: config.brandConfig.name }}
        />
      </Caption>}
    </LayoutContainer>
  </VerticalSpacer>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  offer: state.offer,
  checkIn: state.checkInDate,
  rooms: state.rooms,
  airport: state.airport,
  roomOption: state.roomOption,
  packageOptions: getDurationPackageOptions(state, appState),
  lowestAvailableStayPayTier: getLowestAvailableStayPayTier(state, appState),
  open: !!state.activeFilter,
}), AlwaysOnRoomOptions, preventRenderWithOpenFilter)
